import { useState, type FormEvent } from 'react';
import { Mail, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { api, ApiError } from '../lib/api';

type Props = {
  /** 'card' = compact (sidebar rail), 'inline' = roomier (mid-article on mobile). */
  variant?: 'card' | 'inline';
};

/**
 * Email-capture banner shown on blog articles. Posts to the public newsletter
 * endpoint; subscribers receive the daily blog digest at 09:00.
 */
export function NewsletterBanner({ variant = 'inline' }: Props) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [error, setError] = useState('');

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || state === 'loading') return;
    setState('loading');
    setError('');
    try {
      await api('/api/blog/newsletter/subscribe', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim() }),
      });
      setState('done');
    } catch (err) {
      setState('error');
      setError(err instanceof ApiError ? err.message : 'No se pudo completar la suscripción.');
    }
  }

  const compact = variant === 'card';

  return (
    <div
      className={`rounded-2xl bg-gradient-to-br from-brand-600 to-brand-800 text-white ${
        compact ? 'p-5' : 'p-6 sm:p-7'
      }`}
    >
      <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-white/15">
        <Mail size={20} />
      </div>

      <h3 className={`mt-3 font-bold leading-snug ${compact ? 'text-base' : 'text-lg sm:text-xl'}`}>
        ¡No te pierdas uno de nuestros artículos!
      </h3>
      <p className={`mt-1.5 text-white/80 ${compact ? 'text-xs' : 'text-sm'}`}>
        Únete a los miles de marketers y gestores de redes que reciben nuestros artículos a diario.
      </p>

      {state === 'done' ? (
        <div className="mt-4 flex items-center gap-2 bg-white/15 rounded-xl px-3.5 py-3 text-sm font-medium">
          <CheckCircle2 size={17} className="shrink-0" />
          ¡Listo! Ya estás suscrito.
        </div>
      ) : (
        <form onSubmit={submit} className="mt-4 space-y-2">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@email.com"
            disabled={state === 'loading'}
            className="w-full rounded-xl border-0 px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-white/70 disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={state === 'loading'}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-white text-brand-700 font-semibold text-sm px-4 py-2.5 hover:bg-slate-100 transition disabled:opacity-70"
          >
            {state === 'loading' ? <Loader2 size={15} className="animate-spin" /> : null}
            {state === 'loading' ? 'Suscribiendo…' : 'Suscribirme'}
          </button>
          {state === 'error' && (
            <p className="flex items-center gap-1.5 text-xs text-white bg-red-500/30 rounded-lg px-2.5 py-1.5">
              <AlertTriangle size={13} className="shrink-0" /> {error}
            </p>
          )}
          <p className="text-[11px] text-white/60">Sin spam. Puedes darte de baja cuando quieras.</p>
        </form>
      )}
    </div>
  );
}
